import {
  useCallback,
  useState,
  useEffect,
  useMemo,
  KeyboardEvent,
  memo,
} from "react";
import { useQuery } from "react-query";
import { useSearchParams } from "react-router-dom";
import { Dropdown, Row, Col, Button } from "react-bootstrap";
import { DateTime } from "luxon";

import LedgerActivityTable from "../components/LedgerActivityTable";
import LedgerBalances from "../components/LedgerBalances";
import DateRangePicker from "../components/DateRangePicker";
import PaginationComponent from "../components/Pagination";
import { useRegion } from "../contexts/RegionContext";
import { getLedgerActivity } from "../services/ledgerActivityServices";
import { getUser } from "../services/userServices";
import {
  LedgerActivityCurrency,
  LedgerActivityKind,
  LedgerActivityResult,
  LedgerActivitySearchQuery,
  LedgerActivityStatus,
} from "../types/ledgerActivity";
import { PAGE_SIZE_DEFAULT } from "../constants";

import "../components/Search/index.scss";
import styles from "../styles/ledgerActivity.module.scss";

const ACTIVITY_KINDS: LedgerActivityKind[] = ["LEDGER_ENTRY", "HOLD"];
const HOLD_STATUSES: LedgerActivityStatus[] = [
  "PENDING",
  "SETTLED",
  "REVERSED",
  "EXPIRED",
];

const getCurrencyOptions = (
  region?: string | null
): LedgerActivityCurrency[] => {
  switch (region) {
    case "HK":
      return ["HKD", "USD"];
    case "AE":
      return ["AED", "USD"];
    default:
      return ["SGD", "USD"];
  }
};

const toStartIso = (d: Date | null) =>
  d
    ? DateTime.fromJSDate(d).startOf("day").toUTC().toISO({
        suppressMilliseconds: true,
      }) ?? undefined
    : undefined;

const toEndIso = (d: Date | null) =>
  d
    ? DateTime.fromJSDate(d).endOf("day").toUTC().toISO({
        suppressMilliseconds: true,
      }) ?? undefined
    : undefined;

const LedgerPageHeader = () => (
  <div className={styles.pageHeader}>
    <h1 className={styles.pageTitle}>Ledger activity</h1>
  </div>
);

const CurrencyFilterDropdown = ({
  currency,
  options,
  setCurrency,
}: {
  currency: LedgerActivityCurrency;
  options: LedgerActivityCurrency[];
  setCurrency: (v: LedgerActivityCurrency) => void;
}) => (
  <Dropdown className="add-filters-dropdown m-0">
    <Dropdown.Toggle
      variant="primary"
      className="search-toggle"
      data-testid="ledger-currency-filter-dropdown"
    >
      <span>{currency}</span>
      <span className="material-icons material-icons-outlined">
        chevron_right
      </span>
    </Dropdown.Toggle>
    <Dropdown.Menu>
      {options.map((option) => (
        <Dropdown.Item key={option} onClick={() => setCurrency(option)}>
          {option}
        </Dropdown.Item>
      ))}
    </Dropdown.Menu>
  </Dropdown>
);

const KindFilterDropdown = ({
  kindFilter,
  setKindFilter,
}: {
  kindFilter: LedgerActivityKind | null;
  setKindFilter: (v: LedgerActivityKind | null) => void;
}) => (
  <Dropdown className="add-filters-dropdown m-0">
    <Dropdown.Toggle
      variant="primary"
      className="search-toggle"
      data-testid="ledger-kind-filter-dropdown"
    >
      <span className={kindFilter ? "" : "title-text"}>
        {kindFilter ?? "Kind"}
      </span>
      <span className="material-icons material-icons-outlined">
        chevron_right
      </span>
    </Dropdown.Toggle>
    <Dropdown.Menu>
      {ACTIVITY_KINDS.map((kind) => (
        <Dropdown.Item key={kind} onClick={() => setKindFilter(kind)}>
          {kind}
        </Dropdown.Item>
      ))}
    </Dropdown.Menu>
  </Dropdown>
);

const StatusFilterDropdown = ({
  statusFilter,
  setStatusFilter,
}: {
  statusFilter: LedgerActivityStatus | null;
  setStatusFilter: (v: LedgerActivityStatus | null) => void;
}) => (
  <Dropdown className="add-filters-dropdown m-0">
    <Dropdown.Toggle
      variant="primary"
      className="search-toggle"
      data-testid="ledger-status-filter-dropdown"
    >
      <span className={statusFilter ? "" : "title-text"}>
        {statusFilter ?? "Hold status"}
      </span>
      <span className="material-icons material-icons-outlined">
        chevron_right
      </span>
    </Dropdown.Toggle>
    <Dropdown.Menu>
      {HOLD_STATUSES.map((status) => (
        <Dropdown.Item key={status} onClick={() => setStatusFilter(status)}>
          {status}
        </Dropdown.Item>
      ))}
    </Dropdown.Menu>
  </Dropdown>
);

const UserIdFilterInput = ({
  userIdFilter,
  setUserIdFilter,
  onKeyDown,
}: {
  userIdFilter: string;
  setUserIdFilter: (v: string) => void;
  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
}) => (
  <div className={`search-input ${styles.userIdFilterInput}`}>
    <span className="search-icon material-icons material-icons-outlined">
      person
    </span>
    <input
      data-testid="ledger-user-id-filter"
      type="text"
      className="form-control p-0 bg-transparent"
      placeholder="User ID (required)"
      value={userIdFilter}
      onChange={(e) => setUserIdFilter(e.target.value)}
      onKeyDown={onKeyDown}
    />
  </div>
);

type LedgerSearchSectionProps = {
  dateFrom: Date | null;
  dateTo: Date | null;
  onDateChange: (start: Date | null, end: Date | null) => void;
  onSearch: () => void;
  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
  currency: LedgerActivityCurrency;
  currencyOptions: LedgerActivityCurrency[];
  setCurrency: (v: LedgerActivityCurrency) => void;
  kindFilter: LedgerActivityKind | null;
  setKindFilter: (v: LedgerActivityKind | null) => void;
  statusFilter: LedgerActivityStatus | null;
  setStatusFilter: (v: LedgerActivityStatus | null) => void;
  userIdFilter: string;
  setUserIdFilter: (v: string) => void;
  hasActiveSearch: boolean;
  resultCount?: number;
  onClear: () => void;
};

const LedgerSearchSection = ({
  dateFrom,
  dateTo,
  onDateChange,
  onSearch,
  onKeyDown,
  currency,
  currencyOptions,
  setCurrency,
  kindFilter,
  setKindFilter,
  statusFilter,
  setStatusFilter,
  userIdFilter,
  setUserIdFilter,
  hasActiveSearch,
  resultCount,
  onClear,
}: LedgerSearchSectionProps) => (
  <Col>
    <div className="d-flex">
      <div>
        <div className="mb-1 title-text">Occurred at:</div>
        <Row className="nowrap align-items-md-center m-0">
          <Col className="d-flex align-items-stretch p-0">
            <Row
              className={`nowrap search-input align-items-md-center p-1 ${styles.dateSearchInput}`}
            >
              <DateRangePicker
                startDate={dateFrom}
                endDate={dateTo}
                onChange={onDateChange}
                placeholderText="Select date range"
              />
            </Row>
          </Col>
          <Col>
            <Button
              className="search-btn"
              data-testid="ledger-search-btn"
              variant="success"
              size="lg"
              onClick={onSearch}
            >
              Search
            </Button>
          </Col>
        </Row>
      </div>
      <div className="filter-container">
        <div className="mb-1 title-text">Filter by:</div>
        <div className={styles.filterRow}>
          <UserIdFilterInput
            userIdFilter={userIdFilter}
            setUserIdFilter={setUserIdFilter}
            onKeyDown={onKeyDown}
          />
          <CurrencyFilterDropdown
            currency={currency}
            options={currencyOptions}
            setCurrency={setCurrency}
          />
          <KindFilterDropdown
            kindFilter={kindFilter}
            setKindFilter={setKindFilter}
          />
          <StatusFilterDropdown
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
          />
        </div>
      </div>
    </div>
    {hasActiveSearch && (
      <>
        <hr className="divider" />
        <Row
          className="nowrap search-result align-items-md-center"
          data-testid="ledger-search-result"
        >
          <span>
            {resultCount ?? 0} {resultCount === 1 ? "result" : "results"} on
            this page
          </span>
          <Row
            className="nowrap flex-full align-items-md-center clear-btn"
            onClick={onClear}
            data-testid="ledger-clear-btn"
          >
            <span className="material-icons material-icons-outlined">
              close
            </span>
            <span>Clear search or filters</span>
          </Row>
        </Row>
      </>
    )}
  </Col>
);

const LedgerActivity = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const userIdFromUrl = searchParams.get("userId")?.trim() ?? "";
  const { selectedRegion } = useRegion();

  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [kindFilter, setKindFilter] = useState<LedgerActivityKind | null>(null);
  const [statusFilter, setStatusFilter] = useState<LedgerActivityStatus | null>(
    null
  );
  const [userIdFilter, setUserIdFilter] = useState(userIdFromUrl);
  const [searchQuery, setSearchQuery] = useState<LedgerActivitySearchQuery>();
  const [currentPage, setCurrentPage] = useState(1);

  const trimmedUserId = userIdFilter.trim() || userIdFromUrl;
  const { data: user } = useQuery(
    ["ledgerActivityUser", trimmedUserId],
    () => getUser(trimmedUserId),
    { enabled: Boolean(trimmedUserId) }
  );
  const currencyOptions = useMemo(
    () => getCurrencyOptions(user?.app_country ?? selectedRegion),
    [user?.app_country, selectedRegion]
  );
  const [currency, setCurrency] = useState<LedgerActivityCurrency>(
    currencyOptions[0]
  );

  useEffect(() => {
    if (!currencyOptions.includes(currency)) {
      setCurrency(currencyOptions[0]);
    }
  }, [currency, currencyOptions]);

  const buildQuery = useCallback(
    (
      userId: string,
      selectedCurrency: LedgerActivityCurrency,
      from: Date | null,
      to: Date | null,
      kind: LedgerActivityKind | null,
      status: LedgerActivityStatus | null
    ): LedgerActivitySearchQuery | undefined => {
      const trimmedUserId = userId.trim();
      if (!trimmedUserId) return undefined;

      const query: LedgerActivitySearchQuery = {
        userId: trimmedUserId,
        currency: selectedCurrency,
      };
      if (from || to) {
        query.start = toStartIso(from ?? to);
        query.end = toEndIso(to ?? from);
      }
      if (kind) query.kinds = kind;
      if (status) query.holdStatuses = status;
      return query;
    },
    []
  );

  const onClickSearch = useCallback(() => {
    setCurrentPage(1);
    setSearchQuery(
      buildQuery(
        userIdFilter,
        currency,
        dateFrom,
        dateTo,
        kindFilter,
        statusFilter
      )
    );
  }, [
    userIdFilter,
    currency,
    dateFrom,
    dateTo,
    kindFilter,
    statusFilter,
    buildQuery,
  ]);

  const clearAll = useCallback(() => {
    setDateFrom(null);
    setDateTo(null);
    setKindFilter(null);
    setStatusFilter(null);
    setUserIdFilter("");
    setCurrentPage(1);
    setSearchQuery(undefined);
    if (searchParams.has("userId")) {
      const next = new URLSearchParams(searchParams);
      next.delete("userId");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") onClickSearch();
  };

  useEffect(() => {
    if (!userIdFromUrl) return;
    setUserIdFilter(userIdFromUrl);
    setCurrentPage(1);
    setSearchQuery(
      buildQuery(
        userIdFromUrl,
        currency,
        dateFrom,
        dateTo,
        kindFilter,
        statusFilter
      )
    );
    // Only re-run when the URL userId changes; form values are sampled then.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIdFromUrl, buildQuery]);

  useEffect(() => {
    setCurrentPage(1);
    setSearchQuery((current) =>
      current
        ? buildQuery(
            current.userId,
            currencyOptions.includes(currency) ? currency : currencyOptions[0],
            dateFrom,
            dateTo,
            kindFilter,
            statusFilter
          )
        : undefined
    );
    // Rebuild when region or loaded user's country changes currency options.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRegion, currencyOptions, buildQuery]);

  const { isLoading, error, data } = useQuery<LedgerActivityResult, Error>(
    ["ledgerActivity", currentPage, searchQuery],
    async () =>
      await getLedgerActivity({
        page: currentPage - 1,
        pageSize: PAGE_SIZE_DEFAULT,
        searchQuery: searchQuery!,
      }),
    { enabled: Boolean(searchQuery?.userId) }
  );

  const hasActiveSearch = Boolean(searchQuery?.userId);
  const pageCount = data?.hasMore ? currentPage + 1 : Math.max(currentPage, 1);
  const totalItems =
    (currentPage - 1) * PAGE_SIZE_DEFAULT + (data?.items.length ?? 0);

  return (
    <div>
      <LedgerPageHeader />
      <LedgerSearchSection
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateChange={(start, end) => {
          setDateFrom(start);
          setDateTo(end);
        }}
        onSearch={onClickSearch}
        onKeyDown={handleKeyDown}
        currency={currency}
        currencyOptions={currencyOptions}
        setCurrency={setCurrency}
        kindFilter={kindFilter}
        setKindFilter={setKindFilter}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        userIdFilter={userIdFilter}
        setUserIdFilter={setUserIdFilter}
        hasActiveSearch={hasActiveSearch}
        resultCount={data?.items.length}
        onClear={clearAll}
      />
      <div className={styles.container}>
        <LedgerBalances
          userId={searchQuery?.userId}
          currency={searchQuery?.currency}
        />
        {data && (
          <PaginationComponent
            pageCount={pageCount}
            currentPage={currentPage}
            itemOfPage={data.items.length}
            totalItems={totalItems}
            containerClass={styles.tableTopPagination}
            setCurrentPage={setCurrentPage}
          />
        )}
        <LedgerActivityTable
          data={data}
          error={error}
          isLoading={isLoading}
          idleMessage="Enter a User ID and search to view ledger activity."
        />
        {data && (
          <PaginationComponent
            pageCount={pageCount}
            currentPage={currentPage}
            itemOfPage={data.items.length}
            totalItems={totalItems}
            setCurrentPage={setCurrentPage}
          />
        )}
      </div>
    </div>
  );
};

export default memo(LedgerActivity);
